import React from 'react';
import PackageSelect from 'react-select';
import PackageCreatableSelect from 'react-select/creatable';
import PackageAsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';
import chroma from 'chroma-js';
import tw from 'twin.macro';

import useTheme from '../../hooks/useTheme';

const formatSelectOption = ({ id, name }) => ({
  value: id,
  label: name
});

const getSelectValue = (value, options) => {
  if (!value) {
    return null;
  }

  // Is object { value. label }
  if (value.value) {
    return value;
  }

  const option = options
    .map(formatSelectOption)
    .find(option => option.value === value);

  // Is just value.
  if (option) {
    return option;
  }

  return null;
};

const customStyles = {
  dropdownIndicator: () => tw`flex pl-2`,
  indicatorSeparator: () => tw`bg-gray-300 w-px self-stretch`,
  clearIndicator: styles => ({ ...styles, ...tw`px-2 py-0` }),
  multiValue: styles => ({ ...styles, ...tw`-my-1 mr-1` }),
  input: () => tw`p-0`,
  menu: styles => ({ ...styles }),
  menuPortal: styles => ({ ...styles, zIndex: 9999 }),
  valueContainer: styles => ({ ...styles, ...tw`p-0 overflow-y-auto` }),
  container: styles => ({ ...styles, ...tw`flex-grow` })
};

const formatProps = ({ id, styles, ...props }) => ({
  ...(process.env.NODE_ENV !== 'test' && { menuPortalTarget: document.body }),
  styles: { ...customStyles, ...styles },
  menuPlacement: 'auto',
  ...(id && { inputId: id }),
  ...props
});

const Wrapper = ({ Component, isMulti, ...props }) => {
  const theme = useTheme();

  const styles = {
    multiValueLabel: styles => ({
      ...styles,
      ...tw`font-poppins uppercase leading-none text-xs`,
      color: theme.primary
    }),
    multiValue: styles => ({
      ...styles,
      ...tw`font-poppins uppercase leading-none text-xs my-0`,
      backgroundColor: chroma(theme.primary).alpha(0.25).css()
    }),
    multiValueRemove: styles => ({ ...styles, color: theme.primary }),
    option: (styles, state) => {
      return {
        ...styles,
        boxShadow: 'none',
        background: state.isSelected
          ? theme.primary
          : state.isFocused
            ? chroma(theme.primary).alpha(0.25).css()
            : 'none',
        color: state.isSelected || state.isFocuesed ? 'white' : state.color,
        '&:hover': {
          background:
            state.isSelected || state.isFocuesed
              ? theme.primary
              : chroma(theme.primary).alpha(0.25).css(),
          color: state.isSelected || state.isFocuesed ? 'white' : 'black'
        }
      };
    },
    control: (base, state) => {
      return {
        maxHeight: '64',
        padding: 'calc(0.5rem - 1.5px) calc(0.75rem - 1.5px)',
        boxSizing: 'border-box',
        minWidth: '8rem',
        transition: `border-color 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1),
        background-color 0.3s cubic-bezier(0.16, 1, 0.3, 1)`,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: state.isFocused
          ? `${chroma(theme.primary).alpha(0.5).css()}`
          : '',
        boxShadow: state.isFocused
          ? `0 0 0 0.2rem ${chroma(theme.primary).alpha(0.25)}`
          : 0,
        background: state.isFocused ? 'white' : tw`bg-gray-300`,
        '&:hover': {
          boxShadow: `0 0 0 0.2rem ${chroma(theme.primary).alpha(0.25).css()}`
        },
        '&:active': {
          boxShadow: 'none'
        },
        minHeight: '35px',
        height: '35px',
        ...tw`w-full px-3 flex text-sm rounded bg-gray-100 border border-gray-200 border-solid`
      };
    },
    valueContainer: (base) => ({
      ...base,
      padding: '0 1',
      height: '27.5px',
      marginTop: '-3px',
      flexWrap: 'wrap',
      maxHeight: '100px',
      overflowY: 'auto',
    }),
    input: (base) => ({
      ...base,
      margin: '0',
      marginLeft: '2px'
    })
  };

  return <Component {...formatProps({ styles, isMulti, ...props })} />;
};

Wrapper.propTypes = {
  Component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isMulti: PropTypes.bool
};

const Select = props => <Wrapper Component={PackageSelect} {...props} />;

const CreatableSelect = props => (
  <Wrapper Component={PackageCreatableSelect} {...props} />
);

const AsyncSelect = props => (
  <Wrapper Component={PackageAsyncSelect} {...props} />
);

export {
  Select,
  CreatableSelect,
  AsyncSelect,
  customStyles,
  formatSelectOption,
  getSelectValue
};
